/**
 * AdminFeedbackPage — Admin-only. Lists all feedback with NPS distribution + resolve toggle.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFeedbackPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/feedback`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    try {
      await axios.put(`${API}/feedback/${id}`, { resolved: true }, { withCredentials: true });
      toast.success("Als erledigt markiert");
      load();
    } catch {
      toast.error("Fehler");
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Laedt…</div>;
  if (!data) return null;

  const open = data.items.filter((i) => !i.resolved);
  const closed = data.items.filter((i) => i.resolved);

  return (
    <div data-testid="admin-feedback-page" className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">User-Feedback</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">
            {data.total} Eintraege &middot; Ø {data.avg_rating ?? "—"} / 10 &middot; {open.length} offen
          </p>
        </div>
      </div>

      <Card className="border-[var(--studio-border)] shadow-none">
        <CardHeader><CardTitle className="font-heading text-base">Offen ({open.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2" data-testid="feedback-open-list">
          {open.length === 0 ? (
            <p className="text-xs text-[var(--studio-text-muted)]">Kein offenes Feedback</p>
          ) : open.map((f) => <FeedbackItem key={f.id} f={f} onResolve={resolve} />)}
        </CardContent>
      </Card>

      {closed.length > 0 && (
        <Card className="border-[var(--studio-border)] shadow-none opacity-70">
          <CardHeader><CardTitle className="font-heading text-base">Erledigt ({closed.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {closed.slice(0, 20).map((f) => <FeedbackItem key={f.id} f={f} onResolve={null} />)}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FeedbackItem({ f, onResolve }) {
  const badgeCls = f.rating === null
    ? "bg-gray-100 text-gray-700"
    : f.rating >= 9 ? "bg-green-100 text-green-800"
    : f.rating >= 7 ? "bg-amber-100 text-amber-800"
    : "bg-red-100 text-red-800";
  return (
    <div className="p-3 border border-[var(--studio-border)] rounded-lg" data-testid={`feedback-item-${f.id}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {f.rating !== null && <Badge className={`${badgeCls} border-0`}>{f.rating}/10</Badge>}
          <span className="text-sm font-medium">{f.name || "Anonym"}</span>
          {f.email && <span className="text-xs text-[var(--studio-text-muted)]">{f.email}</span>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[var(--studio-text-muted)] font-mono">
            {f.page || "/"}
          </span>
          {onResolve && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => onResolve(f.id)}
              data-testid={`resolve-${f.id}`}
            >
              <Check className="w-3 h-3 mr-1" /> Erledigt
            </Button>
          )}
        </div>
      </div>
      {f.message && <p className="text-sm mt-2 text-[var(--studio-text)]">{f.message}</p>}
      <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
        {new Date(f.created_at).toLocaleString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
      </p>
    </div>
  );
}
